import clsx from 'clsx';
import { ChangeEvent, ReactElement, useCallback, useContext, useMemo } from 'react';
import { ChevronDown } from 'react-feather';

import globalStyle from '@vestico/shared/src/layouts/utils.module.css';

import { getIsRTL } from '@vestico/i18n';

import { GlobalsContext } from '../CommonAppContext';
import style from './dropdown.module.css';

export interface DropdownOption {
  content: string | ReactElement;
  imageURL?: string;
}

export interface DropdownProps {
  options: DropdownOption[];
  selectedOptionIndex?: number;
  didUpdateOption: (index?: number) => void;
  textColor?: string;
  borderColor?: string;
  minWidth?: number;
  paddingLeft?: number;
  defaultSelectedOption?: string;
  size?: 'small' | 'medium';
}

export const Dropdown = ({
  options,
  didUpdateOption,
  textColor,
  minWidth,
  borderColor,
  paddingLeft,
  selectedOptionIndex,
  defaultSelectedOption,
  size,
}: DropdownProps) => {
  const { stylesConfig } = useContext(GlobalsContext);

  const DEFAULT_SELECT_OPTION_VALUE = -1;
  const updateSelectedOptionIndex = useCallback(
    (index: number) => {
      didUpdateOption(index === DEFAULT_SELECT_OPTION_VALUE ? undefined : index);
    },
    [DEFAULT_SELECT_OPTION_VALUE, didUpdateOption]
  );

  const dropDownCustomStyles = useMemo(() => {
    const styles: Record<string, string> = {};
    if (textColor) {
      styles.color = textColor;
    }
    if (minWidth) {
      styles.minWidth = `${minWidth}px`;
    }
    return styles;
  }, [minWidth, textColor]);

  const selectCustomStyles = useMemo(() => {
    const styles: Record<string, string> = {};
    if (textColor) {
      styles.color = textColor;
    }
    if (borderColor) {
      styles.borderColor = borderColor;
    }
    if (paddingLeft) {
      styles.paddingLeft = `${paddingLeft}px`;
    }
    return styles;
  }, [borderColor, paddingLeft, textColor]);

  const onInputUpdate = useCallback(
    (e: ChangeEvent<HTMLSelectElement>) => {
      updateSelectedOptionIndex(Number(e.currentTarget.value));
    },
    [updateSelectedOptionIndex]
  );

  const isRTL = getIsRTL();
  return (
    <>
      <div className={clsx(style.dropdown, { [style.rtl]: isRTL, [style.small]: size === 'small' })} style={dropDownCustomStyles}>
        <label>
          <select
            value={selectedOptionIndex === undefined && defaultSelectedOption ? DEFAULT_SELECT_OPTION_VALUE : selectedOptionIndex}
            onChange={onInputUpdate}
            className={clsx(globalStyle.textNormal)}
            style={selectCustomStyles}
          >
            {defaultSelectedOption ? (
              <option value={DEFAULT_SELECT_OPTION_VALUE} disabled>
                {defaultSelectedOption}
              </option>
            ) : null}
            {options.map((option: DropdownOption, index: number) => {
              return (
                // eslint-disable-next-line react/no-array-index-key
                <option key={index} value={index}>
                  {option.content}
                </option>
              );
            })}
          </select>
        </label>
        <div className={clsx(style.selectorBox)}>
          <ChevronDown color={textColor ?? stylesConfig.stylesConfig.coreColor} size={20} />
        </div>
      </div>
      <div style={{ clear: 'both' }} />
    </>
  );
};
